import {
  Button, Card, Input, Select, Space, Table, Typography,
} from 'antd';
import rideDriverApi from 'api/rideDriverApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ACCOUNT_STATUS_OPTIONS,
  getRideDriverErrorMessage,
  PROFILE_STATUS_OPTIONS,
  RideDriverAccountStatusTag,
  RideDriverProfileStatusTag,
} from '../../rideDriversConstants';

const { Text, Title } = Typography;

const FILTER_STORAGE_KEY = 'rideDriversListFilter';
const DEFAULT_FILTER = {
  status: '', accountStatus: '', q: '', page: 0, limit: 20,
};

const getInitialFilter = () => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY));
    return {
      ...DEFAULT_FILTER,
      ...(saved && typeof saved === 'object' ? saved : {}),
    };
  } catch (_) {
    return DEFAULT_FILTER;
  }
};

function RideDriversListScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState(getInitialFilter);
  const [searchText, setSearchText] = useState(() => getInitialFilter().q);
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideDriverApi.getDrivers(filter);
      setDrivers(response?.drivers || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setDrivers([]);
      setTotal(0);
      ToastHelper.showError(getRideDriverErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => {
    window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
  }, [filter]);

  const goToDetail = (driverProfileId) => router.navigate(`${driverProfileId}`);

  const columns = useMemo(() => [
    {
      title: 'Mã hồ sơ', dataIndex: 'driverProfileId', width: 100,
      render: (value) => <Text strong>#{value}</Text>,
    },
    {
      title: 'Tài xế', key: 'driver',
      render: (_, row) => (
        <div>
          <div><Text strong>{row?.user?.fullname || 'Chưa có tên'}</Text></div>
          <Text type="secondary">{row?.user?.phone || row?.user?.email || `User #${row?.userId}`}</Text>
        </div>
      ),
    },
    {
      title: 'Hồ sơ KYC', dataIndex: 'status', width: 140,
      render: (value) => <RideDriverProfileStatusTag status={value} />,
    },
    {
      title: 'Tài khoản', key: 'accountStatus', width: 140,
      render: (_, row) => <RideDriverAccountStatusTag status={row?.user?.status} />,
    },
    {
      title: 'Đánh giá', key: 'rating', width: 120,
      render: (_, row) => (row?.ratingCount > 0 ? `${Number(row.ratingAverage).toFixed(1)} ★ (${row.ratingCount})` : '—'),
    },
    {
      title: 'Chuyến hoàn thành', dataIndex: 'completedTripCount', width: 130,
    },
    {
      title: 'Cập nhật', dataIndex: 'updatedAt', width: 170,
      render: (value) => (value ? new Date(value).toLocaleString('vi-VN') : '—'),
    },
    {
      title: '', key: 'action', width: 110,
      render: (_, row) => <Button type="link" onClick={() => goToDetail(row.driverProfileId)}>Xem chi tiết</Button>,
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Danh sách tài xế xe ghép</Title>
            <Text type="secondary">Tổng {total} tài xế theo bộ lọc</Text>
          </div>
          <Button onClick={loadDrivers} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap className="mb-4">
          <Input.Search
            allowClear
            value={searchText}
            placeholder="Tên, email hoặc số điện thoại"
            style={{ width: 320 }}
            onChange={(event) => setSearchText(event.target.value)}
            onSearch={(q) => setFilter((current) => ({ ...current, q: q.trim(), page: 0 }))}
          />
          <Select
            value={filter.status}
            options={PROFILE_STATUS_OPTIONS}
            style={{ width: 200 }}
            onChange={(status) => setFilter((current) => ({ ...current, status, page: 0 }))}
          />
          <Select
            value={filter.accountStatus}
            options={ACCOUNT_STATUS_OPTIONS}
            style={{ width: 200 }}
            onChange={(accountStatus) => setFilter((current) => ({ ...current, accountStatus, page: 0 }))}
          />
        </Space>
        <Table
          rowKey="driverProfileId"
          loading={loading}
          dataSource={drivers}
          columns={columns}
          scroll={{ x: 1100 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: filter.page + 1,
            pageSize: filter.limit,
            total,
            showSizeChanger: true,
            onChange: (page, limit) => setFilter((current) => ({ ...current, page: page - 1, limit })),
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.driverProfileId) })}
        />
      </Card>
    </div>
  );
}

export default RideDriversListScreen;
